import { useInfiniteQuery } from "@tanstack/react-query";
import { apiClient } from "@copro/api";
import { _3_DAYS } from "../../../constants/durations";
import type { ActivityTimelineFeed } from "../../../types";
import { getNextPageIndex, pageRange } from "../../../utils/pagination";
import { keys } from "./keys";
import { OTHER_ACTION_TYPE } from "./useGetCommunications";

export type TimelineFilters = {
  subTopicId?: string;
  providerId?: string;
};

export const TIMELINE_ENTITY_TYPES = ['communication', 'quote', 'event', 'incident', 'document'] as const;

export type TimelineEntityType = (typeof TIMELINE_ENTITY_TYPES)[number];

// "Autre action" rows reach the feed as communications (sub_type 'other') but get their own filter.
export const OTHER_ACTION_FILTER = 'other';

export type TimelineEntityTypeFilter = 'all' | TimelineEntityType | typeof OTHER_ACTION_FILTER;

export const TIMELINE_FILTERS: TimelineEntityTypeFilter[] = ['all', ...TIMELINE_ENTITY_TYPES, OTHER_ACTION_FILTER];

const PAGE_SIZE = 20;

// Column/value pairs scoping the feed to a sub-topic and/or a provider.
export function getTimelineScope(filters: TimelineFilters) {
  const scope: [column: 'subject_id' | 'provider_id', value: string][] = [];

  if (filters.subTopicId) {
    scope.push(['subject_id', filters.subTopicId]);
  }

  if (filters.providerId) {
    scope.push(['provider_id', filters.providerId]);
  }

  return scope;
}

export function isTimelineEnabled(filters: TimelineFilters) {
  return Boolean(filters.subTopicId || filters.providerId);
}

async function getActivityTimelinePage(
  filters: TimelineFilters,
  entityType: TimelineEntityTypeFilter,
  pageIndex: number
): Promise<ActivityTimelineFeed[]> {
  const { from, to } = pageRange(pageIndex, PAGE_SIZE);

  // Secondary order on id keeps pages stable when several items share a timestamp.
  let query = apiClient
    .from('activity_timeline_feed')
    .select('*')
    .order('created_at', { ascending: false, nullsFirst: false })
    .order('id', { ascending: false })
    .range(from, to);

  for (const [column, value] of getTimelineScope(filters)) {
    query = query.eq(column, value);
  }

  if (entityType === OTHER_ACTION_FILTER) {
    query = query.eq('entity_type', 'communication').eq('sub_type', OTHER_ACTION_TYPE);
  } else if (entityType === 'communication') {
    query = query.eq('entity_type', 'communication').neq('sub_type', OTHER_ACTION_TYPE);
  } else if (entityType !== 'all') {
    query = query.eq('entity_type', entityType);
  }

  const { data, error } = await query;

  if (error) {
    throw error;
  }

  return data ?? [];
}

export function useGetActivityTimeline(filters: TimelineFilters, entityType: TimelineEntityTypeFilter = 'all') {
  return useInfiniteQuery({
    queryKey: keys.activityTimelinePages(filters, entityType),
    queryFn: ({ pageParam }) => getActivityTimelinePage(filters, entityType, pageParam),
    initialPageParam: 0,
    getNextPageParam: getNextPageIndex(PAGE_SIZE),
    enabled: isTimelineEnabled(filters),
    staleTime: _3_DAYS,
  });
}
